import { useMemo, useState } from "react";
import { useSession } from "@core/session/SessionProvider";
import { ApiError } from "@core/http";
import type { BilingualText, CatalogEntry, Offer } from "@core/types";
import { tokens } from "@core/theme/tokens";
import {
  CategoryFilterSelect,
  EditorFooter,
  EditorHeader,
  EditorModal,
  type EditorModalTarget,
  EmptyResultsActions,
  EmptyResultsPanel,
  FileUploadChip,
  ImagePreviewBox,
  ImageUploadBox,
  ImpactConfirmDialog,
  includesToFormValues,
  includesToInput,
  IncludesFieldGroup,
  inputStyle,
  LabeledField as OField,
  ListingTablePanel,
  PrizesScreenToolbar,
  resolveBilingualText as text,
  RFieldGroup,
  RowActionsCell,
  RowEditButton,
  submitCreateOrEdit,
  Toggle,
  useApiFieldErrors,
  useEditorForm,
  useIncludesField,
  useToast,
  ValidityRangeFields,
  type ContentLanguage,
} from "@portal/common";
import {
  markErrorHandled,
  reportError,
  reportErrorWithPrefix,
} from "@portal/common/errorMessage";
import { useEntries } from "../catalogs/catalogQueries";
import type { OfferInput } from "./api";
import { useOfferMutations, useOffers } from "./offerQueries";
import {
  OFFER_FORM_FIELDS,
  offerFormSchema,
  type OfferFormValues,
} from "./offerSchemas";

// SPEC-219 §9: mensajes literales que el cliente no puede validar antes de
// enviar (código repetido: depende del catálogo persistido). `FIELDS_REQUIRED`
// y `CTA_REQUIRED` también viven acá como respaldo si el zod de cliente se
// saltea (mismo criterio que `SERVER_ERROR_MESSAGES` en
// benefits/BenefitsScreen.tsx).
//
// C3 (mismo hueco que HomeContentScreen.tsx, code-reviewer): `offerFormSchema`
// no exige español cuando solo se llena el inglés de un campo bilingüe
// OPCIONAL (bajada o nota) — `buildInput` igual manda `{es: "", en: valor}`,
// y `offer.service.ts:72-79` (`isBilingualOrAbsent`) rechaza esa forma con
// `INVALID_REQUEST`. Sin esta entrada, el guardado fallaba sin mensaje.
const INVALID_REQUEST_MESSAGE =
  "La bajada o la nota tienen inglés sin español; completa el español o borra el inglés";

const SERVER_ERROR_MESSAGES: Partial<Record<string, string>> = {
  CODE_DUPLICATE: "Ya existe una oferta con ese código",
  FIELDS_REQUIRED:
    "Título, descripción e imagen son obligatorios para publicar",
  CTA_REQUIRED: "Define el texto del botón que verá el miembro",
  INVALID_REQUEST: INVALID_REQUEST_MESSAGE,
};

// SPEC-219 §7 (D1): `Offer` ya expone `categoryId` (uuid) de lectura —
// corregido en esta misma corrida. Ya no hace falta buscar el catálogo por
// nombre coincidente ni depender de que los nombres de categoría sean
// únicos.
function categoryIdOf(offer: Offer | null): string {
  return offer?.categoryId ?? "";
}

interface OfferImpact {
  readonly linkedPromotions: number;
  readonly openRequests: number;
}
function impactOf(error: ApiError): OfferImpact {
  const impact = error.problem?.impact as Partial<OfferImpact> | undefined;
  return {
    linkedPromotions: impact?.linkedPromotions ?? 0,
    openRequests: impact?.openRequests ?? 0,
  };
}

// SPEC-219 §4: fecha de calendario pura (`format: date`, sin hora ni zona) —
// NUNCA por `new Date(value)` + `toLocaleDateString`: eso interpreta la
// cadena como medianoche UTC y la corre un día hacia atrás según el huso del
// spec visual (mismo hallazgo real documentado en
// points-engine/ruleTableChrome.tsx `formatRuleDate`, aplicado acá con la
// misma técnica, sin importar entre features).
function formatOfferDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

function formatValidity(offer: Offer): string {
  const from = formatOfferDate(offer.validFrom);
  const until = formatOfferDate(offer.validUntil);
  if (!from && !until) return "Indefinida";
  if (from && !until) return `Desde ${from}`;
  if (!from && until) return `Hasta ${until}`;
  return `${from} – ${until}`;
}

interface OffersFilterState {
  search: string;
  categoryId: string;
  status: "all" | "active" | "inactive";
}
const BLANK_FILTER: OffersFilterState = {
  search: "",
  categoryId: "",
  status: "all",
};

function matchesFilter(offer: Offer, filter: OffersFilterState) {
  if (filter.categoryId && filter.categoryId !== offer.categoryId) {
    return false;
  }
  if (filter.status === "active" && !offer.active) return false;
  if (filter.status === "inactive" && offer.active) return false;
  if (filter.search.trim()) {
    const needle = filter.search.trim().toLowerCase();
    const haystack = text(offer.title, "es").toLowerCase();
    if (!haystack.includes(needle)) return false;
  }
  return true;
}

function defaultsFor(offer: Offer | null): OfferFormValues {
  if (!offer) {
    return {
      code: "",
      titleEs: "",
      titleEn: "",
      subtitleEs: "",
      subtitleEn: "",
      descriptionEs: "",
      descriptionEn: "",
      noteEs: "",
      noteEn: "",
      ctaEs: "",
      ctaEn: "",
      categoryId: "",
      imageUrl: "",
      imageFileId: "",
      featured: false,
      includes: [],
      validFrom: "",
      validUntil: "",
      active: true,
    };
  }
  return {
    code: offer.code,
    titleEs: text(offer.title, "es"),
    titleEn: text(offer.title, "en"),
    subtitleEs: text(offer.subtitle ?? null, "es"),
    subtitleEn: text(offer.subtitle ?? null, "en"),
    descriptionEs: text(offer.description, "es"),
    descriptionEn: text(offer.description, "en"),
    noteEs: text(offer.note ?? null, "es"),
    noteEn: text(offer.note ?? null, "en"),
    ctaEs: text(offer.cta ?? null, "es"),
    ctaEn: text(offer.cta ?? null, "en"),
    categoryId: categoryIdOf(offer),
    imageUrl: offer.imageUrl ?? "",
    imageFileId: "",
    featured: offer.featured ?? false,
    includes: includesToFormValues(offer.includes),
    validFrom: offer.validFrom ?? "",
    validUntil: offer.validUntil ?? "",
    active: offer.active,
  };
}

function buildInput(values: OfferFormValues): OfferInput {
  const title: BilingualText = {
    es: values.titleEs,
    ...(values.titleEn ? { en: values.titleEn } : {}),
  };
  const hasSubtitle = values.subtitleEs || values.subtitleEn;
  const subtitle: BilingualText | undefined = hasSubtitle
    ? {
        es: values.subtitleEs,
        ...(values.subtitleEn ? { en: values.subtitleEn } : {}),
      }
    : undefined;
  const description: BilingualText = {
    es: values.descriptionEs,
    ...(values.descriptionEn ? { en: values.descriptionEn } : {}),
  };
  const hasNote = values.noteEs || values.noteEn;
  const note: BilingualText | undefined = hasNote
    ? { es: values.noteEs, ...(values.noteEn ? { en: values.noteEn } : {}) }
    : undefined;
  const cta: BilingualText = {
    es: values.ctaEs,
    ...(values.ctaEn ? { en: values.ctaEn } : {}),
  };
  return {
    // PA-037-D: `code` viaja siempre, incluso al editar — el servicio lo
    // descarta y conserva el persistido.
    code: values.code,
    title,
    ...(subtitle ? { subtitle } : {}),
    description,
    ...(note ? { note } : {}),
    cta,
    categoryId: values.categoryId,
    ...(values.imageUrl ? { imageUrl: values.imageUrl } : {}),
    // PLAN-007 Fase 4d: viaja solo cuando el operador subió un archivo nuevo
    // en esta sesión del formulario; el backend le da prioridad sobre
    // `imageUrl` (OfferInput.imageFileId) y a partir de ahí firma la URL en
    // cada lectura.
    ...(values.imageFileId ? { imageFileId: values.imageFileId } : {}),
    featured: values.featured,
    includes: includesToInput(values.includes),
    ...(values.validFrom ? { validFrom: values.validFrom } : {}),
    ...(values.validUntil ? { validUntil: values.validUntil } : {}),
    active: values.active,
  };
}

interface OfferEditorProps {
  offer: Offer | null;
  categories: CatalogEntry[] | undefined;
  categoriesLoading: boolean;
  canWrite: boolean;
  submitting: boolean;
  onSubmit: (id: string | undefined, input: OfferInput) => void;
  onCancel: () => void;
  // Revisión en vivo 2026-09-22, puntos #3/#4A: el editor ES el diálogo. La
  // pantalla ya no lo envuelve en un `Modal`, le pasa acá el destino.
  modal: EditorModalTarget;
  // Mismo criterio que `CatalogEntryForm`/`RewardEditor`: `subtitleEs`/
  // `noteEs` vacíos con su `en` lleno (`offer.service.ts`, `fieldRejection`)
  // viven en `OffersScreen` (dueño del `useToast`); acá se intenta asociar
  // con `applyApiFieldErrors` y, si no se puede, se devuelve por
  // `onUnhandledSubmitError` para el toast de siempre.
  submitError?: unknown;
  onUnhandledSubmitError?: (error: unknown) => void;
}

function OfferEditor({
  offer,
  categories,
  categoriesLoading,
  canWrite,
  submitting,
  onSubmit,
  onCancel,
  modal,
  submitError,
  onUnhandledSubmitError,
}: OfferEditorProps) {
  const [language, setLanguage] = useState<ContentLanguage>("es");
  const { register, handleSubmit, watch, setValue, errors, setError } =
    useEditorForm<OfferFormValues>(defaultsFor(offer));
  useApiFieldErrors(
    submitError,
    setError as (
      field: string,
      error: { type: string; message: string },
    ) => void,
    OFFER_FORM_FIELDS,
    onUnhandledSubmitError,
  );
  const titleEs = watch("titleEs");
  const active = watch("active");
  const featured = watch("featured");
  const imageUrl = watch("imageUrl");
  const includes = watch("includes");

  const {
    addItem: addInclude,
    removeItem: removeInclude,
    updateItem: updateInclude,
  } = useIncludesField(includes, (next) =>
    setValue("includes", next, { shouldDirty: true }),
  );

  const submit = (values: OfferFormValues) => {
    const parsed = offerFormSchema.safeParse(values);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        setError(issue.path[0] as keyof OfferFormValues, {
          message: issue.message,
        });
      }
      return;
    }
    onSubmit(offer?.id, buildInput(parsed.data));
  };

  const requests = offer?.requests;

  return (
    <EditorModal
      modal={modal}
      onSubmit={handleSubmit(submit)}
      header={
        <EditorHeader
          eyebrow={offer ? "Editar oferta" : "Nueva oferta"}
          title={titleEs || "Oferta sin título"}
          language={language}
          onLanguageChange={setLanguage}
          statusIndicator={
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <Toggle
                on={active}
                onChange={(next) =>
                  setValue("active", next, { shouldDirty: true })
                }
                ariaLabel="Activa"
              />
              <span
                style={{
                  font: `500 12px ${tokens.font.ui}`,
                  color: active ? tokens.text.secondary : tokens.text.disabled,
                }}
              >
                {active ? "Activa" : "Inactiva"}
              </span>
            </div>
          }
        />
      }
      footer={
        <EditorFooter
          onCancel={onCancel}
          action={offer ? "canEdit" : "canCreate"}
          disabled={submitting}
          submitting={submitting}
        />
      }
    >
      {/* El `padding` del cuerpo lo pone ahora la región del `Modal`
        (`values.modalRegions.bodyPadding`, los mismos 20px 24px que tenía
        este `fieldset`): si se dejara acá quedaría duplicado. */}
      <fieldset
        disabled={!canWrite}
        style={{ border: "none", margin: 0, padding: 0 }}
      >
        {/* SPEC-219 §3/§10 criterio 1, PA-037-D: único campo editable solo
            al crear — de solo lectura al editar. El body igual lo manda
            (el contrato lo exige en ambos verbos); el servidor lo descarta
            al editar y conserva el persistido. */}
        <OField label="Código" required error={errors.code?.message}>
          <input
            {...register("code")}
            readOnly={Boolean(offer)}
            aria-readonly={Boolean(offer)}
            style={{
              ...inputStyle,
              ...(offer
                ? { color: tokens.text.tertiary, cursor: "not-allowed" }
                : {}),
            }}
          />
        </OField>
        <OField
          label="Título"
          required={language === "es"}
          error={language === "es" ? errors.titleEs?.message : undefined}
        >
          <input
            key={language}
            {...register(language === "es" ? "titleEs" : "titleEn")}
            placeholder={
              language === "en"
                ? "Sin traducir · se mostrará en español"
                : undefined
            }
            style={inputStyle}
          />
        </OField>
        <OField
          label="Bajada"
          error={language === "es" ? errors.subtitleEs?.message : undefined}
        >
          <input
            key={language}
            {...register(language === "es" ? "subtitleEs" : "subtitleEn")}
            placeholder={
              language === "en"
                ? "Sin traducir · se mostrará en español"
                : undefined
            }
            style={inputStyle}
          />
        </OField>
        <OField
          label="Descripción"
          required={language === "es"}
          hint="Es lo que el miembro lee en el detalle de la oferta."
          error={language === "es" ? errors.descriptionEs?.message : undefined}
        >
          <textarea
            key={language}
            {...register(language === "es" ? "descriptionEs" : "descriptionEn")}
            placeholder={
              language === "en"
                ? "Sin traducir · se mostrará en español"
                : undefined
            }
            style={{ ...inputStyle, minHeight: 84, resize: "vertical" }}
          />
        </OField>
        {/* T5 (qa-bug-matrix-2026-09.md §4.1, #17): un ítem de "Qué
            incluye" sin texto en español entra por `includeSchema`
            (offerSchemas.ts). El error se ve acá, donde está el campo, en
            vez de que el operador lea el mensaje de INVALID_REQUEST de
            bajada/nota, que no tiene nada que ver. Mismo patrón que T2 en
            benefits/BenefitsScreen.tsx. T5 intento 2: el `FieldError` ahora
            lo dibuja `IncludesFieldGroup` (prop `error`), no esta pantalla —
            ver su comentario de cabecera. */}
        <IncludesFieldGroup
          label="Qué incluye"
          hint="Lista ordenada de lo que comprende la oferta."
          items={includes}
          language={language}
          onAdd={addInclude}
          onRemove={removeInclude}
          onUpdate={updateInclude}
          error={errors.includes?.message}
        />
        <OField
          label="Nota de condiciones"
          error={language === "es" ? errors.noteEs?.message : undefined}
        >
          <textarea
            key={language}
            {...register(language === "es" ? "noteEs" : "noteEn")}
            placeholder={
              language === "en"
                ? "Sin traducir · se mostrará en español"
                : undefined
            }
            style={{ ...inputStyle, minHeight: 64, resize: "vertical" }}
          />
        </OField>
        <OField
          label="Llamado a la acción"
          required={language === "es"}
          hint="El texto es editable; siempre genera una solicitud."
          error={language === "es" ? errors.ctaEs?.message : undefined}
        >
          <input
            key={language}
            {...register(language === "es" ? "ctaEs" : "ctaEn")}
            placeholder={
              language === "en"
                ? "Sin traducir · se mostrará en español"
                : undefined
            }
            style={inputStyle}
          />
        </OField>
        <OField label="Categoría" required error={errors.categoryId?.message}>
          <select {...register("categoryId")} style={inputStyle}>
            <option value="">Selecciona una categoría</option>
            {categoriesLoading ? (
              <option value="" disabled>
                Cargando…
              </option>
            ) : (
              categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {text(category.name, "es")}
                </option>
              ))
            )}
          </select>
        </OField>
        <RFieldGroup
          label="Imagen"
          hint="Se muestra en el catálogo y en el detalle. Sin texto incrustado."
        >
          <ImageUploadBox>
            <ImagePreviewBox imageUrl={imageUrl} />
            <FileUploadChip
              entityType="offer"
              label="Reemplazar imagen"
              ariaLabel="Subir imagen de la oferta"
              onUploaded={(image) => {
                // PLAN-007 Fase 4d: `imageUrl` solo para la
                // previsualización inmediata (vigente 15 minutos); el
                // `fileId` es lo que de verdad viaja al servidor.
                setValue("imageUrl", image.imageUrl, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setValue("imageFileId", image.fileId, {
                  shouldDirty: true,
                });
              }}
            />
          </ImageUploadBox>
          {errors.imageUrl?.message ? (
            <div
              role="alert"
              style={{
                font: `500 11.5px ${tokens.font.ui}`,
                color: tokens.danger.base,
                marginTop: 4,
              }}
            >
              {errors.imageUrl.message}
            </div>
          ) : null}
        </RFieldGroup>
        <RFieldGroup
          label="Destacada"
          hint="Marca de prioridad en el listado de la app."
        >
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <Toggle
              on={featured}
              onChange={(next) =>
                setValue("featured", next, { shouldDirty: true })
              }
              ariaLabel="Destacada"
            />
            <span
              style={{
                font: `500 12px ${tokens.font.ui}`,
                color: featured ? tokens.text.secondary : tokens.text.disabled,
              }}
            >
              {featured ? "Sí" : "No"}
            </span>
          </div>
        </RFieldGroup>
        <ValidityRangeFields
          validFrom={register("validFrom")}
          validUntil={register("validUntil")}
          untilError={errors.validUntil?.message}
        />
        {/* SPEC-219 §7/§10 criterio 7: conteo de solicitudes generadas y
            abiertas por oferta. En alta (sin `offer`) todavía no existen
            solicitudes: se muestra en cero. */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 14px",
            borderRadius: 12,
            border: `1px solid ${tokens.surface.line2}`,
            background: tokens.surface.surface,
          }}
        >
          <span className="label">Solicitudes generadas · abiertas</span>
          <span
            style={{
              font: `600 13px ${tokens.font.ui}`,
              color: tokens.text.primary,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {requests?.total ?? 0} · {requests?.open ?? 0}
          </span>
        </div>
      </fieldset>
    </EditorModal>
  );
}

// Las dos causas de rechazo al inactivar (OFFER_LINKED, OFFER_HAS_REQUESTS)
// abren el mismo diálogo de impacto con distinto conteo y etiqueta —
// deduplicado acá dentro de OffersScreen.tsx, sin tocar otras pantallas.
function OfferInactivateImpactDialog({
  open,
  offerTitle,
  affectedCount,
  affectedLabel,
  onCancel,
  onConfirm,
  busy,
}: {
  open: boolean;
  offerTitle: string;
  affectedCount: number;
  affectedLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
}) {
  return (
    <ImpactConfirmDialog
      open={open}
      eyebrow="Ofertas"
      title={`¿Inactivar la oferta «${offerTitle}»?`}
      affectedCount={affectedCount}
      affectedLabel={affectedLabel}
      onCancel={onCancel}
      onConfirm={onConfirm}
      confirmLabel="Sí, inactivar"
      busy={busy}
    />
  );
}

const COLS = "minmax(220px,1.3fr) 150px 165px 110px 130px 160px";

export function OffersScreen() {
  const toast = useToast();
  const { hasPermission } = useSession();
  const offers = useOffers();
  const categories = useEntries("offer-categories", false, "");
  const mutations = useOfferMutations();
  const [filter, setFilter] = useState<OffersFilterState>(BLANK_FILTER);
  const [editing, setEditing] = useState<Offer | null | undefined>(undefined);
  const [pendingInactivate, setPendingInactivate] = useState<{
    offer: Offer;
    impact: OfferImpact;
    reason: "OFFER_LINKED" | "OFFER_HAS_REQUESTS";
  } | null>(null);
  // Mismo criterio que `RewardsScreen`/`MembersScreen`: `subtitleEs`/`noteEs`
  // vacíos con su `en` lleno ahora identifican el campo — se lo pasamos a
  // `OfferEditor` para que `applyApiFieldErrors` lo asocie; si no puede, cae
  // al toast de siempre (`onUnhandledSubmitError`).
  const [submitError, setSubmitError] = useState<unknown>(undefined);
  const canCreate = hasPermission("content", "canCreate");
  const canEdit = hasPermission("content", "canEdit");
  const canInactivate = hasPermission("content", "canInactivate");

  const list = offers.data;
  const rows = useMemo(
    () => (list ?? []).filter((offer) => matchesFilter(offer, filter)),
    [list, filter],
  );

  const submitting = mutations.create.isPending || mutations.edit.isPending;

  const onSaved = (offer: Offer) => {
    setEditing(undefined);
    toast.show(`${text(offer.title, "es")} guardada y publicada`);
    for (const warning of offer.warnings ?? [])
      toast.show(warning.message, "neutral");
  };

  // T2 (qa-bug-matrix-2026-09.md §4.1, #18): `if (message)` descartaba en
  // silencio cualquier código sin mapear — reemplazado por el resolutor
  // compartido, que siempre habla y agrega el identificador de correlación
  // cuando no hay texto mapeado.
  const handleUnhandledOfferSubmitError = (error: unknown) =>
    reportError(toast, error, SERVER_ERROR_MESSAGES);
  const onSubmit = (id: string | undefined, input: OfferInput) => {
    // Se marca atendido en el mismo tick: el editor lo asocia a su campo o
    // lo devuelve por `handleUnhandledOfferSubmitError` en un efecto, que
    // puede correr después del macrotask del fallback global — sin esto, un
    // rechazo ya mostrado bajo su campo sumaba además el toast genérico.
    submitCreateOrEdit(id, input, mutations, onSaved, (error) => {
      markErrorHandled(error);
      setSubmitError(error);
    });
  };

  const clearFilters = () => setFilter(BLANK_FILTER);
  const hasAnyOffer = (list?.length ?? 0) > 0;

  // SPEC-219 §5/§9/§10 criterios 5 y 6: primera llamada sin `confirmed`. Si
  // el servidor responde 409 `OFFER_LINKED`/`OFFER_HAS_REQUESTS`, se abre el
  // diálogo de impacto con el conteo real en vez de inactivar de una.
  const requestInactivation = (offer: Offer) => {
    mutations.inactivate.mutate(
      { id: offer.id, confirmed: false },
      {
        onSuccess: () => toast.show(`${text(offer.title, "es")} inactivada`),
        onError: (error) => {
          if (
            error instanceof ApiError &&
            (error.code === "OFFER_LINKED" ||
              error.code === "OFFER_HAS_REQUESTS")
          ) {
            setPendingInactivate({
              offer,
              impact: impactOf(error),
              reason: error.code,
            });
            markErrorHandled(error);
            return;
          }
          reportError(toast, error, SERVER_ERROR_MESSAGES);
        },
      },
    );
  };
  const confirmInactivation = () => {
    if (!pendingInactivate) return;
    mutations.inactivate.mutate(
      { id: pendingInactivate.offer.id, confirmed: true },
      {
        onSuccess: () => {
          toast.show(`${text(pendingInactivate.offer.title, "es")} inactivada`);
          setPendingInactivate(null);
        },
        onError: (error) => reportError(toast, error, SERVER_ERROR_MESSAGES),
      },
    );
  };

  // Reactivar reconstruye el input a partir de los mismos helpers que usa el
  // editor (`defaultsFor` + `buildInput`), en vez de repetir a mano la forma
  // de `OfferInput` una segunda vez — mismo criterio que
  // `BenefitsScreen.toggleActive`.
  const reactivate = (offer: Offer) => {
    const input = {
      ...buildInput(defaultsFor(offer)),
      active: true,
    };
    const title = text(offer.title, "es");
    mutations.edit.mutate(
      { id: offer.id, input },
      {
        onSuccess: (updated) => {
          toast.show(`${text(updated.title, "es")} activada`);
          for (const warning of updated.warnings ?? [])
            toast.show(warning.message, "neutral");
        },
        // T2 (qa-bug-matrix-2026-09.md §4.4, #30/#35): sin `onError` el
        // conmutador volvía solo a su posición sin decir nada.
        onError: (error) => {
          reportErrorWithPrefix(toast, `No se pudo reactivar ${title}.`, error);
        },
      },
    );
  };

  // SEC-05 (human-resolution-sec-05-toggle-reactivation-permission.json,
  // corregido en SPEC-211/SPEC-210): mover `active` en cualquier dirección
  // exige `canInactivate` en el servidor
  // (require-inactivate-for-active-change.ts no distingue dirección) — un
  // Operador (`canEdit` sin `canInactivate`) no ve forma de inactivar NI de
  // reactivar. `canEdit` sigue alcanzando solo para "Editar", que no mueve
  // `active`.
  const toggleActive = (offer: Offer) => {
    if (offer.active) {
      requestInactivation(offer);
      return;
    }
    reactivate(offer);
  };

  return (
    <div>
      {/* NAV-01 (human-resolution-nav-01-offers-tab.json): Ofertas comparte
        el encabezado y la fila de pestañas de "Gestión de Premios"
        (PortalPrimitives.tsx), con el cuarto chip que esa resolución
        autoriza. */}
      <PrizesScreenToolbar
        filter={filter}
        setFilter={setFilter}
        searchPlaceholder="Buscar oferta"
        extraFilters={
          <CategoryFilterSelect
            value={filter.categoryId}
            onChange={(categoryId) =>
              setFilter((prev) => ({ ...prev, categoryId }))
            }
            categories={categories.data}
          />
        }
        createLabel="Nueva oferta"
        onCreate={() => setEditing(null)}
      />

      <ListingTablePanel
        loading={offers.isLoading}
        rows={rows}
        columns={COLS}
        headers={["Título", "Categoría", "Vigencia", "Estado", "Solicitudes"]}
        empty={
          <EmptyResultsPanel
            icon="gift"
            title={
              hasAnyOffer ? "Ninguna oferta coincide" : "Aún no hay ofertas"
            }
            description={
              hasAnyOffer
                ? "Ajuste la búsqueda, la categoría o el estado."
                : "Las ofertas exclusivas aparecen en la app apenas exista una activa y vigente."
            }
            actions={
              <EmptyResultsActions
                hasAny={hasAnyOffer}
                onClearFilters={clearFilters}
                onCreate={() => setEditing(null)}
                createLabel="Nueva oferta"
              />
            }
          />
        }
        renderRow={(offer) => {
          const canToggle = canInactivate;
          return (
            <div
              key={offer.id}
              style={{
                display: "grid",
                gridTemplateColumns: COLS,
                gap: 14,
                alignItems: "center",
                padding: "13px 24px",
                borderBottom: `1px solid ${tokens.surface.line}`,
                opacity: offer.active ? 1 : 0.55,
              }}
            >
              <span
                style={{
                  font: `600 12.5px/1.35 ${tokens.font.ui}`,
                  color: tokens.text.primary,
                }}
              >
                {text(offer.title, "es")}
              </span>
              <span
                style={{
                  font: `400 12px/1.5 ${tokens.font.ui}`,
                  color: tokens.text.secondary,
                }}
              >
                {offer.category}
              </span>
              <span
                style={{
                  font: `400 12px/1.5 ${tokens.font.ui}`,
                  color: tokens.text.tertiary,
                }}
              >
                {formatValidity(offer)}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <Toggle
                  on={offer.active}
                  onChange={() => toggleActive(offer)}
                  disabled={!canToggle}
                  ariaLabel="Activa"
                />
                <span
                  style={{
                    font: `500 11px ${tokens.font.ui}`,
                    color: offer.active
                      ? tokens.text.secondary
                      : tokens.text.disabled,
                  }}
                >
                  {offer.active ? "Activa" : "Inactiva"}
                </span>
              </div>
              <span
                style={{
                  font: `500 12px ${tokens.font.ui}`,
                  color: tokens.text.secondary,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {offer.requests?.total ?? 0} · {offer.requests?.open ?? 0}
              </span>
              <RowActionsCell>
                <RowEditButton
                  visible={canEdit}
                  onClick={() => setEditing(offer)}
                />
              </RowActionsCell>
            </div>
          );
        }}
      />

      {editing !== undefined ? (
        <OfferEditor
          key={editing?.id ?? "new"}
          offer={editing}
          categories={categories.data}
          categoriesLoading={categories.isLoading}
          canWrite={canCreate || canEdit}
          submitting={submitting}
          onSubmit={onSubmit}
          onCancel={() => setEditing(undefined)}
          modal={{
            label: editing?.id ? "Editar oferta" : "Nueva oferta",
            width: 660,
            onClose: () => setEditing(undefined),
          }}
          submitError={submitError}
          onUnhandledSubmitError={handleUnhandledOfferSubmitError}
        />
      ) : null}

      {/* SPEC-219 §5/§9/§10 criterio 5: promociones del inicio que quedarían
        sin destino. */}
      <OfferInactivateImpactDialog
        open={pendingInactivate?.reason === "OFFER_LINKED"}
        offerTitle={
          pendingInactivate ? text(pendingInactivate.offer.title, "es") : ""
        }
        affectedCount={pendingInactivate?.impact.linkedPromotions ?? 0}
        affectedLabel="promociones del inicio enlazan a esta oferta y dejarán de ser interactivas"
        onCancel={() => setPendingInactivate(null)}
        onConfirm={confirmInactivation}
        busy={mutations.inactivate.isPending}
      />
      {/* SPEC-219 §4/§9/§10 criterio 6: solicitudes abiertas que no se
        cierran — el interés de una persona no caduca porque la oferta haya
        terminado. */}
      <OfferInactivateImpactDialog
        open={pendingInactivate?.reason === "OFFER_HAS_REQUESTS"}
        offerTitle={
          pendingInactivate ? text(pendingInactivate.offer.title, "es") : ""
        }
        affectedCount={pendingInactivate?.impact.openRequests ?? 0}
        affectedLabel="solicitudes abiertas. Inactivarla no las cierra"
        onCancel={() => setPendingInactivate(null)}
        onConfirm={confirmInactivation}
        busy={mutations.inactivate.isPending}
      />
    </div>
  );
}
